/**
 * What the user is told when an upload fails, and what the logs may keep of a failed chunk's answer
 * (prod fixes 2026-10-02, follow-up fu-r2: FU1-01, FU1-03, FU1-04, UXFU1-01..04).
 *
 * One table, read by both surfaces that can tell the user: `FileUploadManager.reportUploadFailure` (every tool page,
 * the one dialog per failure) and `FileUploadService.uploadFileFromLocalDisk` (callers that drive the service directly,
 * e.g. the meme generator). UX-SPEC §1: say what happened, then what to do; never a status code, an error class name
 * or the server's own `message` -- those stay on the error, in FILE_UPLOAD_ERROR and in the logs.
 *
 * Leaf module on purpose (it imports only `ConnectivityUtils`), so the service and the manager can both import it
 * without an import cycle.
 */
import { ConnectivityUtils } from './connectivity.utils';

/**
 * Marker property set on the error thrown when the chunk-upload finalize GET (`original-input-files`) answers with no
 * data. Carried on the error rather than parsed out of its message so the classification cannot drift when someone
 * rewords the message. (Moved here from `file-upload.service.ts`, which re-exports it.)
 */
export const FINALIZE_MISSING_MARKER = '__xcFinalizeMissing';

/**
 * Copy for a chunk refused by something between the user and our edge (see `chunkFailureError`); copy deck XU-1t / XU-1.
 * "Something on your network", not "your network": the page is the only evidence, and it does not say whose filter it is.
 */
export const UPLOAD_BLOCKED_TITLE = 'Upload blocked';
export const UPLOAD_BLOCKED_COPY = "Something on your network blocked this upload before it reached us. If you're on a work or school network, try a different one.";

/** True for the rejection `chunkFailureError` gives a chunk refused on the way (a proxy's block page). */
export function isUploadBlockedOnTheWay(err: unknown): boolean {
    return !!err && (err as any).blockedOnTheWay === true;
}

/** The quiet (Studio) path's offline sentence; the loud dialog reuses it (UXFU1-04) so both say the same thing. */
export const UPLOAD_OFFLINE_COPY = 'You are offline — files cannot be added until you are back.';

export type UploadFailureKind = 'blocked' | 'offline' | 'connectivity' | 'service' | 'refused';

export interface UploadFailureNotice {
    kind: UploadFailureKind;
    title: string;
    text: string;
    /** Offer Retry. False only where another try is known to meet the same answer (a proxy's block page). */
    retry: boolean;
}

const NOTICES: Record<UploadFailureKind, UploadFailureNotice> = {
    blocked: { kind: 'blocked', title: UPLOAD_BLOCKED_TITLE, text: UPLOAD_BLOCKED_COPY, retry: false },
    // UXFU1-04: offline is the user's connection, not our server. The manager retries the rows itself on `online`.
    offline: {
        kind: 'offline', title: "You're offline",
        text: `${UPLOAD_OFFLINE_COPY} The upload will start again when your connection is back.`,
        retry: true,
    },
    // Deck copy (r4): online, but no edge answered. UX main recheck (P4): the title in sentence case, like "Upload failed".
    connectivity: { kind: 'connectivity', title: 'Server unreachable', text: 'The upload server is temporarily unavailable. Please try again.', retry: true },
    // 5xx and the finalize answer that never came: our side (UX-SPEC §1 "Blame", XR-1's wording).
    service: { kind: 'service', title: 'Upload failed', text: 'The upload stopped because of a hiccup on our side, not your file. Please try again.', retry: true },
    // Any other refusal of the request (4xx from our stack). Not the server's message, not the status.
    refused: { kind: 'refused', title: 'Upload failed', text: "We couldn't upload this file. Try again, or pick a different file.", retry: true },
};

/**
 * A name with a space in it was written for people (`'File is empty'`, `'File is too large'`): `handleError` shows such
 * an error as it is. A class name (`ClientError`, `TimeoutError`, `Error`) never is (UXFU1-02).
 */
export function hasHumanName(err: any): boolean {
    return typeof err?.name === 'string' && /\s/.test(err.name.trim());
}

/**
 * The notice for a failed upload, or null when this table does not know the error (the caller falls back to
 * `UIContext.handleError`, which now never titles a modal with a class name).
 *
 * Order matters: a deliberately human error (an empty file) is shown as itself even offline; a proxy's block page is
 * the one answer that says what happened even if the browser has since gone offline; then "offline" beats every
 * transport class (UXFU1-04: a lost connection must not blame our server).
 */
export function uploadFailureNotice(err: any, online: boolean = ConnectivityUtils.isOnline()): UploadFailureNotice | null {
    if (hasHumanName(err)) return null;
    if (isUploadBlockedOnTheWay(err)) return NOTICES.blocked;
    if (!online) return NOTICES.offline;
    if (ConnectivityUtils.isConnectivityError(err)) return NOTICES.connectivity;
    if (err?.name === 'ServiceError' || err?.[FINALIZE_MISSING_MARKER]) return NOTICES.service;
    if (err?.name === 'ClientError') return NOTICES.refused;
    return null;
}

/** The notice of one kind, for a caller that knows the class better than the error does (a failed edge resolve). */
export function uploadFailureNoticeOfKind(kind: UploadFailureKind): UploadFailureNotice {
    return NOTICES[kind];
}

/** The copy a service-level caller shows when the table does not know the error (retires "Could not update the file"). */
export const UPLOAD_FAILED_FALLBACK: UploadFailureNotice = NOTICES.refused;

/**
 * What a log line may say about a chunk's response body (FU1-04). A proxy's block page (Zscaler and the like) can carry
 * the user's name, email, client IP and the blocked URL, so the body itself is never logged: only its class, at most the
 * page's `<title>` (80 chars, with anything shaped like an email address or an IP address masked), and for JSON only
 * ironman's own `message` field (our text, already the error's message).
 */
export function describeResponseBody(body: unknown): string {
    if (typeof body !== 'string' || body.trim() === '') return 'body=empty';
    const t = body.trimStart();
    if (t.charAt(0) === '{' || t.charAt(0) === '[') {
        let msg = '';
        try {
            const m = JSON.parse(t)?.message;
            if (typeof m === 'string') msg = m.length <= 300 ? m : `${m.slice(0, 300)}…`;
        } catch { return `body=json(unparsed, ${t.length} chars)`; }
        return msg ? `body=json message="${maskPersonal(msg)}"` : 'body=json';
    }
    if (t.charAt(0) === '<') {
        const m = /<title[^>]*>([\s\S]*?)<\/title>/i.exec(t);
        const title = m ? maskPersonal(m[1].replace(/\s+/g, ' ').trim()).slice(0, 80) : '';
        return title ? `body=html title="${title}"` : 'body=html';
    }
    return `body=text(${t.length} chars)`;
}

function maskPersonal(s: string): string {
    return s
        .replace(/[^\s@<>"']+@[^\s@<>"']+\.[^\s@<>"']+/g, '[email]')
        .replace(/\b\d{1,3}(?:\.\d{1,3}){3}\b/g, '[ip]')
        .replace(/\b(?:[0-9a-f]{1,4}:){3,7}[0-9a-f]{1,4}\b/gi, '[ip]');
}
